<template>
    <v-modal :is-open="isOpen"
        class="bg-white"
        @close="$emit('close')">
        <v-modal-header @close="$emit('close')">
            {{ t('shipping-cost') }}
        </v-modal-header>

        <div class="space-y-lg px-md pb-20 lg:px-10">
            <label class="flex h-11 items-center rounded-2xs
                border border-gray-100 focus-within:border-gray-200">
                <v-icon icon="search"
                    class="mx-4 text-2xl" />

                <div class="flex-1">
                    <input v-model="postalCode"
                        type="text"
                        class="size-full bg-transparent outline-none"
                        :placeholder="t('default-postal-code')">
                </div>
            </label>

            <div v-if="postalCode.length < 4"
                class="space-y-xs rounded-2xs bg-gray-100 p-sm">
                <p class="font-bold">{{ t('not-found.title') }}</p>

                <p>{{ t('not-found.description') }}</p>
            </div>

            <div class="space-y-lg">
                <v-page-product-delivery-group v-for="group in groups"
                    :key="group.id"
                    :title="group.title"
                    icon="shipping">
                    <v-page-product-delivery-disclosure v-for="method in group.children"
                        :key="method.id"
                        :method="method"
                        :is-free-shipping="isFreeShipping" />
                </v-page-product-delivery-group>
            </div>
        </div>
    </v-modal>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import type { ViewProductShippingMethodsQuery } from '~~/graphql';

const properties = defineProps<{
    modelValue: string
    isOpen: boolean
    groups: ShippingMethodGroupCombined[]
    isFreeShipping: boolean
}>();

const emit = defineEmits<{
    (event: 'close'): void
    (event: 'update:modelValue', value: string): void
}>();

const { t } = useI18n();

type Product = Extract<ViewProductShippingMethodsQuery['product'], { __typename: 'SimpleProduct' }>;

type ShippingMethod = Product['availableShippingMethods'][0];
type ShippingMethodGroup = NonNullable<ShippingMethod['group']>;
type ShippingMethodGroupCombined = ShippingMethodGroup & {
    children: ShippingMethod[]
};

const postalCode = useVModel(properties, 'modelValue', emit);
</script>

<i18n lang="json">
{
    "et": {
        "shipping-cost": "Tarne",
        "default-postal-code": "10001",
        "not-found": {
            "title": "Hinnad postiindeksile 10001",
            "description": "Sisestades postiindeksi, näete tarne hinda enda valitud piirkonda."
        }
    },
    "fi": {
        "shipping-cost": "Toimituskulut",
        "default-postal-code": "00100",
        "not-found": {
            "title": "Esimerkkihinnat postinumerolle 00100",
            "description": "Syöttämällä postinumerosi näet toimitusalueesi hinnat."
        }
    }
}
</i18n>
